import { forwardRef, useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, MonitorPlay, Scan } from "lucide-react";
import { playSfx } from "@/components/editor/StickerSfxPanel";
import { StickerLayer } from "@/components/editor/StickerLayer";
import { SubsPreview, useMusicSync } from "@/components/editor/LivePreview";
import { ResultCanvas } from "@/components/editor/ResultCanvas";
import { FinalPreview } from "@/components/editor/FinalPreview";
import { useI18n } from "@/context/I18nContext";
import { API, ASPECTS, fmtTimePrecise } from "@/lib/api";

let dropsCache = null;
const useDrops = () => {
  const [d, setD] = useState(dropsCache || {});
  useEffect(() => {
    if (dropsCache) return;
    fetch(`${API}/fx/catalog`).then((r) => r.json()).then((c) => { dropsCache = Object.fromEntries((c.music || []).map((m) => [m.id, m.drop || 0])); setD(dropsCache); }).catch(() => {});
  }, []);
  return d;
};

const frameRect = (sa, ta, cx, cy = 0.5, z = 1, crop = false) => {
  if (crop) {
    const s = z * Math.max(ta / sa, 1);
    const wf = Math.min(1, ta / (s * sa)) * 100, hf = Math.min(1, 1 / s) * 100;
    return { width: `${wf}%`, height: `${hf}%`, left: `${(100 - wf) * cx}%`, top: `${(100 - hf) * cy}%` };
  }
  if (ta < sa) {
    const w = (ta / sa) * 100;
    return { width: `${w}%`, height: "100%", top: 0, left: `${(100 - w) * cx}%` };
  }
  const h = (sa / ta) * 100;
  return { width: "100%", height: `${h}%`, left: 0, top: `${(100 - h) / 2}%` };
};

const videoFilter = (fx, nearImpact) => {
  const f = [];
  if (fx.includes("bloom")) f.push("brightness(1.08) saturate(1.2) contrast(1.05)");
  if (fx.includes("bw_hit") && nearImpact) f.push("grayscale(1)");
  if (fx.includes("neon")) f.push("saturate(1.45) contrast(1.12) hue-rotate(-8deg)");
  if (fx.includes("chroma")) f.push("drop-shadow(-3px 0 0 rgba(255,0,70,0.65)) drop-shadow(3px 0 0 rgba(0,210,255,0.65))");
  return f.join(" ") || "none";
};

const useFit = (ratio) => {
  const wrap = useRef(null);
  const [dim, setDim] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const fit = () => {
      const W = el.clientWidth - 8, H = el.clientHeight - 8;
      if (W <= 0 || H <= 0) return;
      setDim(W / H > ratio ? { w: Math.round(H * ratio), h: H } : { w: W, h: Math.round(W / ratio) });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ratio]);
  return [wrap, dim];
};

const Overlays = ({ cfg, fx, range, current, rel, inSel, playing, flash, fade, box, setCfg, selected, setSelected }) => (
  <>
    {fx.includes("neon_frame") && <div className="absolute inset-1 rounded-lg border-[3px] border-[#7a5cff] shadow-[0_0_18px_#2f7bff,inset_0_0_18px_#a746ff] pointer-events-none" data-testid="fx-preview-frame" />}
    {fx.includes("cinema") && <><div className="absolute inset-x-0 top-0 h-[10%] bg-black pointer-events-none" /><div className="absolute inset-x-0 bottom-0 h-[10%] bg-black pointer-events-none" data-testid="fx-preview-cinema" /></>}
    <SubsPreview cfg={cfg} range={range} current={current} box={box} setCfg={setCfg} playing={playing} />
    <StickerLayer cfg={cfg} setCfg={setCfg} rel={rel} inSel={inSel} playing={playing} box={box} selected={selected} setSelected={setSelected} />
    {fx.includes("watermark") && <img src="/logo-wide.png" alt="" className="absolute top-[3%] right-[4%] w-[26%] pointer-events-none" data-testid="fx-preview-watermark" />}
    {fx.includes("hook") && cfg.hook && (!inSel || rel < 3.2) && (
      <div className="absolute inset-x-[4%] top-[12%] text-center font-display font-bold uppercase text-white leading-tight pointer-events-none" data-testid="fx-preview-hook"
        style={{ fontSize: "clamp(12px, 2.4vw, 30px)", textShadow: "0 0 3px #08081a, 0 0 3px #08081a, 0 0 14px #a746ff, 0 0 22px #2f7bff" }}>{cfg.hook}</div>
    )}
    <div className="absolute inset-0 pointer-events-none bg-white" style={{ opacity: flash }} />
    <div className="absolute inset-0 pointer-events-none bg-black" style={{ opacity: fade }} />
  </>
);

export const Player = forwardRef(({ src, video, cfg, setCfg, onTime, current = 0, range, playing, onPlayState, selected, setSelected, getBody }, ref) => {
  const { t } = useI18n();
  const box = useRef(null);
  const drops = useDrops();
  const [mode, setModeS] = useState(() => localStorage.getItem("clipx_player_mode") || "result");
  const setMode = (m) => { localStorage.setItem("clipx_player_mode", m); setModeS(m); };
  const [muted, setMuted] = useState(false);
  const sa = video.width && video.height ? video.width / video.height : 16 / 9;
  const ta = ASPECTS.find((a) => a.id === cfg.aspect)?.ratio || 9 / 16;
  const result = mode === "result";
  const [wrap, dim] = useFit(result ? ta : sa);
  const fx = cfg.effects || [];
  const frame = frameRect(sa, ta, cfg.fit === "crop" ? cfg.crop_x : 0.5, cfg.crop_y ?? 0.5, cfg.zoom || 1, cfg.fit === "crop");
  const inSel = current >= range.start && current <= range.end;
  const rel = current - range.start;
  const flash = fx.includes("flash") && playing && inSel && rel < 0.35 ? 1 - rel / 0.35 : 0;
  const fade = fx.includes("fadeout") && playing && inSel && range.end - current < 0.6 ? 1 - (range.end - current) / 0.6 : 0;
  const impact = cfg.impact ?? (range.end - range.start) / 3;
  const dImp = rel - impact;
  const nearImpact = inSel && dImp >= 0 && dImp < 0.5;
  const punch = fx.includes("punch") && inSel ? 0.32 * Math.exp(-Math.pow(dImp * 5, 2)) : 0;
  const zoom = (fx.includes("zoom") && inSel ? 1 + 0.22 * Math.min(1, rel / Math.max(1, range.end - range.start)) : 1) + punch;
  const quaking = fx.includes("quake") && playing && inSel && Math.abs(dImp) < 0.8;
  const shake = (fx.includes("shake") && playing && inSel) || quaking ? "cx-shake" : "";
  const look = { filter: videoFilter(fx, nearImpact), transform: `scale(${zoom})`, transition: "transform 0.25s linear" };
  const prevRel = useRef(rel);
  useEffect(() => {
    if (playing && inSel) (cfg.sfx || []).forEach((s) => { if (prevRel.current < s.t && rel >= s.t && rel - s.t < 0.6) playSfx(s.id, 0.85 * (s.vol ?? 1)); });
    prevRel.current = rel;
  }, [rel, playing, inSel, cfg.sfx]);

  useMusicSync({ cfg, vref: ref, rel, inSel, playing, range, drops });
  const slow = fx.includes("slowmo");
  useEffect(() => { if (ref?.current) ref.current.playbackRate = slow ? 0.5 : 1; }, [slow, ref]);
  useEffect(() => { if (ref?.current) ref.current.muted = muted; }, [muted, ref]);
  const toggle = () => { const v = ref.current; if (!v) return; if (v.paused) { if (!inSel) v.currentTime = range.start; v.play().catch(() => {}); } else v.pause(); };
  const ov = { cfg, fx, range, current, rel, inSel, playing, flash, fade, box, setCfg, selected, setSelected };

  return (
    <div className="flex flex-col gap-2 h-full min-h-0" data-testid="player">
      <div ref={wrap} className="cx-brackets relative rounded-xl bg-[#020308] border border-white/10 overflow-hidden h-[34vh] md:h-[52vh] lg:h-auto lg:flex-1 lg:min-h-[300px]" data-testid="player-wrap">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-md shadow-[0_0_40px_rgba(58,215,255,0.08)]" style={{ width: dim.w || "100%", height: dim.h || "100%" }} data-testid="player-stage" data-mode={mode}>
          <div className={`absolute inset-0 ${result ? "" : shake}`}>
            <video ref={ref} src={src} controls={!result} playsInline preload="auto" data-testid="editor-video-player" onTimeUpdate={(e) => onTime(e.currentTarget.currentTime)}
              onPlay={() => onPlayState?.(true)} onPause={() => onPlayState?.(false)}
              className={`absolute inset-0 w-full h-full ${result ? "opacity-0 pointer-events-none" : ""}`} style={result ? undefined : look} />
          </div>
          {result ? (
            <>
              <div className={`absolute inset-0 overflow-hidden ${shake}`} onClick={toggle}><ResultCanvas vref={ref} cfg={cfg} style={look} /></div>
              {fx.includes("vignette") && <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.75) 100%)" }} data-testid="fx-preview-vignette" />}
              {!playing && <button onClick={toggle} data-testid="player-big-play" className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-black/55 border border-white/20 backdrop-blur grid place-items-center hover:scale-105 transition-transform"><Play className="w-6 h-6 text-white ml-0.5" /></button>}
              <div ref={box} className="absolute inset-0 pointer-events-none" style={{ containerType: "inline-size" }} data-testid="result-frame"><Overlays {...ov} /></div>
            </>
          ) : (
            <>
              {fx.includes("vignette") && <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.75) 100%)" }} data-testid="fx-preview-vignette" />}
              <div className={`absolute pointer-events-none transition-[left,width,height,top] duration-200 ${cfg.fit === "crop" ? "border-2 border-[#3ad7ff] shadow-[0_0_0_9999px_rgba(4,5,10,0.6)]" : "border border-dashed border-white/40"}`} style={{ ...frame, containerType: "inline-size" }} data-testid="crop-overlay" ref={box}>
                <span className="absolute top-0 left-0 bg-[#3ad7ff] text-black text-[10px] font-mono font-bold px-1.5 py-0.5 z-10">{cfg.aspect}</span>
                <Overlays {...ov} />
              </div>
            </>
          )}
          {slow && <span className="absolute bottom-3 left-3 text-[10px] font-mono bg-black/70 border border-white/10 px-2 py-1 rounded pointer-events-none">x0.5</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 min-w-0" data-testid="player-controls">
        <button onClick={toggle} data-testid="player-play-btn" className="cx-btn rounded-full w-9 h-9 grid place-items-center">{playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}</button>
        <button onClick={() => setMuted((m) => !m)} data-testid="player-mute-btn" className="cx-ghost rounded-full w-9 h-9 grid place-items-center">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
        <span className="font-mono text-[11px] text-slate-400 whitespace-nowrap" data-testid="player-time">{fmtTimePrecise(Math.max(0, rel))} / {fmtTimePrecise(range.end - range.start)}</span>
        <div className="flex rounded-full cx-metal-rail p-0.5 ml-auto" data-testid="player-mode-toggle">
          <button onClick={() => setMode("result")} data-testid="player-mode-result" className={`rounded-full px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${result ? "cx-metal cx-metal-active text-white" : "text-slate-400 hover:text-white"}`}><MonitorPlay className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t("pl.result")}</span></button>
          <button onClick={() => setMode("source")} data-testid="player-mode-source" className={`rounded-full px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${!result ? "cx-metal cx-metal-active text-white" : "text-slate-400 hover:text-white"}`}><Scan className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t("pl.source")}</span></button>
        </div>
        {getBody && <FinalPreview getBody={getBody} testid="player-final-preview-btn" label={<><span className="hidden sm:inline">{t("pl.final")}</span><span className="sm:hidden">6 s</span></>}
          className="cx-ghost rounded-full px-3 py-2 text-[11px] font-semibold text-white flex items-center gap-1.5 whitespace-nowrap" />}
      </div>
    </div>
  );
});
Player.displayName = "Player";
